/**
 * Reports on individual eBay sold listings.
 *
 * PokeTrace flags outliers by price. It cannot tell that a listing is the wrong
 * card, or that the title describes something other than what shipped. The
 * person holding the card can, in a second, which is the only reason a report
 * button ever gets used.
 *
 * Two rules shape everything here. Your own report applies to you immediately,
 * because you said it and it is your collection. Three distinct people are
 * needed before a sale stops counting for everybody, because a cheap comp drags
 * a collection's value down and one person should not be able to erase it.
 */

import { supabase } from '../lib/supabase';

export const REASONS = [
  { id: 'wrong_card',  label: 'Wrong card' },
  { id: 'wrong_price', label: 'Wrong price' },
  { id: 'wrong_title', label: 'Title does not match' },
  { id: 'other',       label: 'Something else' },
];

/** Stable across a vendor change, unlike PokeTrace's own listing id. */
export function itemIdFromUrl(url) {
  const match = String(url || '').match(/\/itm\/(\d+)/);
  return match ? match[1] : null;
}

/** How a card is addressed in the reports table. */
export function cardKeyOf(card) {
  if (!card) return null;
  return card.tcgdex_card_id
    || card.tcg_id
    || [card.name, card.set_number, card.set_name].filter(Boolean).join('|').toLowerCase()
    || null;
}

/**
 * Everything this viewer should hold out for a card: their own reports, plus
 * whatever three or more people have agreed on.
 *
 * Never throws. A pricing screen that cannot reach the reports table should
 * show the vendor's numbers, not an error.
 *
 * @returns {Promise<{ mine: Set<string>, consensus: Set<string> }>}
 */
export async function fetchHeldOut(card) {
  const key = cardKeyOf(card);
  const empty = { mine: new Set(), consensus: new Set(), excluded: new Set() };
  if (!key) return empty;

  try {
    const [own, agreed, mineExcluded] = await Promise.all([
      supabase.from('pricing_sale_reports')
        .select('source_item_id')
        .eq('card_key', key)
        .neq('status', 'rejected'),
      supabase.rpc('held_out_sales', { p_card_key: key }),
      supabase.from('pricing_sale_excludes')
        .select('source_item_id')
        .eq('card_key', key),
    ]);

    return {
      mine: new Set((own.data || []).map((r) => r.source_item_id)),
      consensus: new Set((agreed.data || []).map((r) => r.source_item_id)),
      excluded: new Set((mineExcluded.data || []).map((r) => r.source_item_id)),
    };
  } catch (err) {
    console.warn('[SaleReport] read failed:', err?.message);
    return empty;
  }
}

/**
 * File a report.
 *
 * @param {Object} args
 * @param {Object} args.card      the card being viewed
 * @param {Object} args.sale      the evidence row being reported
 * @param {string} args.tier      "NEAR_MINT", "PSA_10"
 * @param {string} args.reason    one of REASONS
 * @param {string} [args.note]    free text, required in spirit for 'other'
 * @param {number} [args.tierPrice] what we were showing for the tier, snapshotted
 * @returns {Promise<{ ok: boolean, itemId: string|null, error?: string }>}
 */
export async function reportSale({ card, sale, tier, reason, note, tierPrice }) {
  const itemId = itemIdFromUrl(sale?.url);
  const key = cardKeyOf(card);
  if (!itemId || !key || !reason) return { ok: false, itemId: null, error: 'missing details' };

  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return { ok: false, itemId, error: 'not signed in' };

    const { error } = await supabase.from('pricing_sale_reports').insert({
      user_id: userId,
      source_item_id: itemId,
      listing_url: sale.url || null,
      card_key: key,
      card_name: card.name || null,
      set_name: card.set_name || null,
      set_number: card.set_number || null,
      tier,
      reason,
      note: note?.trim() || null,
      listing_price: sale.price,
      listing_title: sale.title || null,
      // Snapshot. Whether a user's reports only ever point downward is
      // unanswerable later without this, and that is the failure mode.
      tier_price: tierPrice ?? null,
    });

    // Already reported by this user. The unique index doing its job.
    if (error && error.code === '23505') return { ok: true, itemId };
    if (error) return { ok: false, itemId, error: error.message };
    return { ok: true, itemId };
  } catch (err) {
    return { ok: false, itemId, error: err?.message || 'failed' };
  }
}

/** Undo. Someone who reports the wrong row should not be stuck with it. */
export async function unreportSale({ card, sale }) {
  const itemId = itemIdFromUrl(sale?.url);
  const key = cardKeyOf(card);
  if (!itemId || !key) return { ok: false };
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user?.id) return { ok: false };
    const { error } = await supabase.from('pricing_sale_reports')
      .delete()
      .eq('user_id', auth.user.id)
      .eq('source_item_id', itemId);
    return { ok: !error };
  } catch {
    return { ok: false };
  }
}

/**
 * Drop one sale out of your own numbers. No reason, no review, no vote.
 *
 * Deliberately not a report. A report says the listing is wrong and three of
 * them stop it counting for everybody; this says only "not in mine". Keeping
 * them apart is what lets the consensus threshold keep meaning agreement
 * rather than traffic.
 *
 * @returns {Promise<{ ok: boolean, itemId: string|null, error?: string }>}
 */
export async function excludeSale({ card, sale, tier }) {
  const itemId = itemIdFromUrl(sale?.url);
  const key = cardKeyOf(card);
  if (!itemId || !key) return { ok: false, itemId: null, error: 'missing details' };

  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return { ok: false, itemId, error: 'not signed in' };

    const { error } = await supabase.from('pricing_sale_excludes').insert({
      user_id: userId,
      source_item_id: itemId,
      card_key: key,
      tier,
      listing_price: sale.price,
      listing_title: sale.title || null,
      listing_url: sale.url || null,
    });

    // Already excluded. The unique index doing its job, and the caller wanted
    // it excluded, so this is the state they asked for.
    if (error && error.code === '23505') return { ok: true, itemId };
    if (error) return { ok: false, itemId, error: error.message };
    return { ok: true, itemId };
  } catch (err) {
    return { ok: false, itemId, error: err?.message || 'failed' };
  }
}

/** Put it back. The second tap on the same button. */
export async function unexcludeSale({ card, sale }) {
  const itemId = itemIdFromUrl(sale?.url);
  const key = cardKeyOf(card);
  if (!itemId || !key) return { ok: false };
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user?.id) return { ok: false };
    const { error } = await supabase.from('pricing_sale_excludes')
      .delete()
      .eq('user_id', auth.user.id)
      .eq('source_item_id', itemId);
    return { ok: !error };
  } catch {
    return { ok: false };
  }
}

export default {
  REASONS, fetchHeldOut, reportSale, unreportSale,
  excludeSale, unexcludeSale, itemIdFromUrl, cardKeyOf,
};
